import type L from 'leaflet';

// Entrance for a defense the user just drew: a line or outline draws itself
// along its length, an area's fill fades in, a point scales in. Plain DOM
// work on the SVG paths Leaflet already made, with inline styles removed
// again afterwards so the later setStyle calls (hover, selection) and
// Leaflet's own redraws on zoom see a path exactly as they would have.
//
// Inline styles rather than setStyle: Leaflet writes stroke-dasharray as an
// SVG attribute, and a CSS property on the element beats an attribute, so
// this can't fight it and setStyle can't undo it mid-animation.

const DURATION = 'var(--redi-dur-slow, 280ms)';
const EASING = 'var(--redi-ease-standard, cubic-bezier(0.2, 0, 0, 1))';
// Longer than the slowest transition, so a path whose transitionend never
// fires (element detached, tab hidden) still gets cleaned up.
const SAFETY_MS = 480;

type PathLayer = L.Path;

function pathEl(layer: PathLayer): SVGPathElement | null {
  const el = layer.getElement?.();
  return el && 'getTotalLength' in el ? (el as SVGPathElement) : null;
}

function afterTransition(el: SVGElement, cleanup: () => void): void {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    el.removeEventListener('transitionend', onEnd);
    cleanup();
  };
  const onEnd = (e: Event) => {
    if (e.target === el) finish();
  };
  const timer = setTimeout(finish, SAFETY_MS);
  el.addEventListener('transitionend', onEnd);
}

/** Strokes the path in from nothing along its length. A dashed line has no
 *  solid stroke to grow, so it fades in instead. */
export function drawStrokeIn(layer: PathLayer, dashed: boolean): void {
  const el = pathEl(layer);
  if (!el) return;
  if (dashed) {
    fadeIn(el, 'opacity', '');
    return;
  }
  let length = 0;
  try {
    length = el.getTotalLength();
  } catch {
    return;
  }
  if (!length || !Number.isFinite(length)) return;
  el.style.strokeDasharray = `${length}`;
  el.style.strokeDashoffset = `${length}`;
  void el.getBoundingClientRect(); // commit the start state before the transition
  el.style.transition = `stroke-dashoffset ${DURATION} ${EASING}`;
  el.style.strokeDashoffset = '0';
  afterTransition(el, () => {
    el.style.removeProperty('stroke-dasharray');
    el.style.removeProperty('stroke-dashoffset');
    el.style.removeProperty('transition');
  });
}

/** Fades one CSS property in from 0. `to` is the resting value to
 *  transition to; empty means "whatever the element resolves to". */
function fadeIn(el: SVGElement, property: 'opacity' | 'fill-opacity', to: string): void {
  el.style.setProperty(property, '0');
  void el.getBoundingClientRect();
  el.style.transition = `${property} ${DURATION} ${EASING}`;
  el.style.setProperty(property, to || '1');
  afterTransition(el, () => {
    el.style.removeProperty(property);
    el.style.removeProperty('transition');
  });
}

/** An area: outline draws itself, fill fades up to its resting opacity. */
export function drawAreaIn(layer: PathLayer, fillOpacity: number): void {
  const el = pathEl(layer);
  if (!el) return;
  let length = 0;
  try {
    length = el.getTotalLength();
  } catch {
    length = 0;
  }
  el.style.fillOpacity = '0';
  if (length && Number.isFinite(length)) {
    el.style.strokeDasharray = `${length}`;
    el.style.strokeDashoffset = `${length}`;
  }
  void el.getBoundingClientRect();
  el.style.transition = `stroke-dashoffset ${DURATION} ${EASING}, fill-opacity ${DURATION} ${EASING}`;
  el.style.strokeDashoffset = '0';
  el.style.fillOpacity = `${fillOpacity}`;
  afterTransition(el, () => {
    for (const prop of ['stroke-dasharray', 'stroke-dashoffset', 'fill-opacity', 'transition']) {
      el.style.removeProperty(prop);
    }
  });
}

/** Adds the scale-in class (leafletHelpers MARKER_FEEL_CSS) and takes it
 *  off when the animation ends, so a later class change can't replay it. */
export function popIn(el: Element | null | undefined): void {
  if (!el) return;
  el.classList.add('redi-plan-enter');
  const remove = () => el.classList.remove('redi-plan-enter');
  el.addEventListener('animationend', remove, { once: true });
  setTimeout(remove, SAFETY_MS);
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
